import { HTTP_STATUS, VALIDATION_MESSAGES } from '../constants';

export interface AppError {
  message: string;
  code?: string;
  status?: number;
  stack?: string;
  timestamp: Date;
}

/**
 * アプリケーション共通エラークラス
 */
export class ApplicationError extends Error {
  public code?: string;
  public status?: number;
  public timestamp: Date;

  constructor(message: string, code?: string, status?: number) {
    super(message);
    this.name = 'ApplicationError';
    this.code = code;
    this.status = status;
    this.timestamp = new Date();
  }
}

/**
 * HTTPエラーをユーザーフレンドリーなメッセージに変換
 */
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof ApplicationError) {
    return error.message;
  }

  if (error instanceof Error) {
    // ネットワークエラー
    if (error.message.includes('NetworkError') || error.message.includes('fetch')) {
      return VALIDATION_MESSAGES.NETWORK_ERROR;
    }
    
    // HTTP エラー
    if (error.message.includes('HTTP Error:')) {
      const statusMatch = error.message.match(/HTTP Error: (\d+)/);
      if (statusMatch && statusMatch[1]) {
        const status = parseInt(statusMatch[1]);
        return getHttpStatusMessage(status);
      }
    }

    return error.message;
  }

  return VALIDATION_MESSAGES.SERVER_ERROR;
};

/**
 * HTTPステータスコードからユーザーメッセージを取得
 */
export const getHttpStatusMessage = (status: number): string => {
  switch (status) {
    case HTTP_STATUS.BAD_REQUEST:
      return '入力データに問題があります。入力内容を確認してください。';
    case HTTP_STATUS.UNAUTHORIZED:
      return 'ログインが必要です。再度ログインしてください。';
    case HTTP_STATUS.FORBIDDEN:
      return 'この操作を実行する権限がありません。';
    case HTTP_STATUS.NOT_FOUND:
      return '要求されたリソースが見つかりません。';
    case HTTP_STATUS.INTERNAL_SERVER_ERROR:
      return 'サーバーエラーが発生しました。しばらく時間をおいてお試しください。';
    default:
      return `サーバーエラー (${status}) が発生しました。`;
  }
};

/**
 * APIレスポンスエラーをハンドリング
 */
export const handleApiError = async (response: Response): Promise<never> => {
  let errorMessage = getHttpStatusMessage(response.status);
  
  try {
    const errorData = await response.json();
    if (errorData.message) {
      errorMessage = errorData.message;
    } else if (errorData.error) {
      errorMessage = errorData.error;
    }
  } catch {
    // JSONパースエラーは無視してデフォルトメッセージを使用
  }

  throw new ApplicationError(errorMessage, 'API_ERROR', response.status);
};

/**
 * エラーをログに記録（本番環境では外部サービスに送信も可能）
 */
export const logError = (error: Error | AppError, context?: Record<string, unknown>): void => {
  const errorInfo = {
    message: error.message,
    stack: error.stack,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
    context,
  };

  if (process.env.NODE_ENV === 'development') {
    console.error('Application Error:', errorInfo);
  }

  // 本番環境では外部エラー追跡サービス（Sentry等）に送信
  // if (process.env.NODE_ENV === 'production') {
  //   // 将来的にSentry等の実装をここに追加
  // }
};

/**
 * 非同期関数のエラーハンドリング用ラッパー
 */
export const withErrorHandling = <T extends unknown[], R>(
  fn: (...args: T) => Promise<R>,
  fallbackValue?: R
) => {
  return async (...args: T): Promise<R | undefined> => {
    try {
      return await fn(...args);
    } catch (error) {
      logError(error instanceof Error ? error : new Error(String(error)));
      return fallbackValue;
    }
  };
}; 